import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { platformChatService } from '../../../services/PlatformChatService';
import { userService } from '../../../services/UserService';
import type {
  ChatMember,
  InvitableCompanyUser,
} from '../../../types/chatMember';
import type {
  ChatSession,
  ChatSessionEntity,
} from '../../../types/chatSession';
import type { ChatSessionsContextValue } from '../../../types/chatSessionsContext';
import { tenantQueryKeys } from '../../tenant/tenantQueryKeys';
import { chatQueryKeys } from '../chatQueryKeys';
import {
  useCreatePlatformChatSessionMutation,
  useDeletePlatformChatSessionMutation,
  useInvitePlatformChatParticipantMutation,
  usePinPlatformChatSessionMutation,
  useRemovePlatformChatParticipantMutation,
  useRenamePlatformChatSessionMutation,
  useSetPlatformChatSessionSystemPromptMutation,
  useTransferPlatformChatOwnershipMutation,
  useUnpinPlatformChatSessionMutation,
} from '../mutations/usePlatformChatMutations';
import {
  buildDirectoryUserById,
  getIsoTimestamp,
} from './chatSessionListMapping';

type UseChatSessionActionsParams = {
  currentUserId: string | undefined;
  sessions: ChatSession[];
  sessionsById: Map<string, ChatSessionEntity>;
};

export function useChatSessionActions({
  currentUserId,
  sessions,
  sessionsById,
}: UseChatSessionActionsParams) {
  const queryClient = useQueryClient();
  const createSessionMutation = useCreatePlatformChatSessionMutation();
  const deleteSessionMutation = useDeletePlatformChatSessionMutation();
  const pinSessionMutation = usePinPlatformChatSessionMutation();
  const unpinSessionMutation = useUnpinPlatformChatSessionMutation();
  const renameSessionMutation = useRenamePlatformChatSessionMutation();
  const setSessionSystemPromptMutation =
    useSetPlatformChatSessionSystemPromptMutation();
  const inviteParticipantMutation = useInvitePlatformChatParticipantMutation();
  const removeParticipantMutation = useRemovePlatformChatParticipantMutation();
  const transferOwnershipMutation = useTransferPlatformChatOwnershipMutation();

  const loadLatestParticipants = useCallback(
    async (sessionId: string) =>
      platformChatService.listParticipants(sessionId),
    [],
  );

  const loadDirectoryUsers = useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: tenantQueryKeys.users(),
        queryFn: () => userService.listUsers(),
      }),
    [queryClient],
  );

  const refreshSessions = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: chatQueryKeys.sessions() });
  }, [queryClient]);

  const getSessionById = useCallback(
    (id: string) => sessions.find((session) => session.id === id),
    [sessions],
  );

  const createSession = useCallback(
    async (title: string): Promise<ChatSession> => {
      const created = await createSessionMutation.mutateAsync(title);
      await queryClient.refetchQueries({
        queryKey: chatQueryKeys.sessions(),
      });
      const ownerId = currentUserId ?? '';
      return {
        id: created.id,
        title: created.title ?? '',
        lastMessage: '',
        isPinned: false,
        sharedAs: null,
        updatedAt: getIsoTimestamp(created.stamp ?? created.created),
        ownerId: created.ownerUserId ?? ownerId,
        systemPrompt: created.systemPrompt ?? '',
        participantCount: 1,
        mostRecentMessageId: null,
        mostRecentAuthorUserId: null,
        mostRecentRole: null,
        hasNew: false,
      };
    },
    [createSessionMutation, currentUserId, queryClient],
  );

  const deleteSession = useCallback<ChatSessionsContextValue['deleteSession']>(
    async (sessionId) => {
      await deleteSessionMutation.mutateAsync(sessionId);
    },
    [deleteSessionMutation],
  );

  const leaveSession = useCallback<ChatSessionsContextValue['leaveSession']>(
    async (sessionId) => {
      const participants = await loadLatestParticipants(sessionId);
      const currentParticipant = participants.find(
        (participant) => participant.userId === currentUserId,
      );

      if (!currentParticipant) {
        return;
      }

      await removeParticipantMutation.mutateAsync({
        sessionId,
        participantId: currentParticipant.id,
      });
    },
    [currentUserId, loadLatestParticipants, removeParticipantMutation],
  );

  const togglePin = useCallback<ChatSessionsContextValue['togglePin']>(
    async (sessionId) => {
      const session = sessions.find(
        (sessionItem) => sessionItem.id === sessionId,
      );

      if (session?.isPinned) {
        await unpinSessionMutation.mutateAsync(sessionId);
        return;
      }

      await pinSessionMutation.mutateAsync(sessionId);
    },
    [pinSessionMutation, sessions, unpinSessionMutation],
  );

  const renameSession = useCallback<ChatSessionsContextValue['renameSession']>(
    async (sessionId, title) => {
      const session = sessionsById.get(sessionId);
      if (!session) {
        return;
      }
      await renameSessionMutation.mutateAsync({ session, title });
    },
    [renameSessionMutation, sessionsById],
  );

  const setSessionSystemPrompt = useCallback<
    ChatSessionsContextValue['setSessionSystemPrompt']
  >(
    async (sessionId, systemPrompt) => {
      await setSessionSystemPromptMutation.mutateAsync({
        sessionId,
        systemPrompt,
      });
    },
    [setSessionSystemPromptMutation],
  );

  const listChatMembers = useCallback<
    ChatSessionsContextValue['listChatMembers']
  >(
    async (sessionId): Promise<ChatMember[]> => {
      const [participants, directoryUsers] = await Promise.all([
        loadLatestParticipants(sessionId),
        loadDirectoryUsers(),
      ]);
      const directoryUserById = buildDirectoryUserById(directoryUsers);

      return participants.map((participant) => {
        const directoryUser = directoryUserById.get(participant.userId);
        return {
          userId: participant.userId,
          name:
            directoryUser?.firstName && directoryUser?.lastName
              ? `${directoryUser.firstName} ${directoryUser.lastName}`.trim()
              : (directoryUser?.email ?? participant.userId),
          email: directoryUser?.email ?? '',
          companyRole: 'member',
          chatRole: participant.role === 'OWNER' ? 'owner' : 'participant',
          thumbnail: directoryUser?.thumbnail ?? null,
        };
      });
    },
    [loadDirectoryUsers, loadLatestParticipants],
  );

  const listInvitableCompanyUsers = useCallback<
    ChatSessionsContextValue['listInvitableCompanyUsers']
  >(
    async (sessionId, searchQuery = ''): Promise<InvitableCompanyUser[]> => {
      const [members, directoryUsers] = await Promise.all([
        listChatMembers(sessionId),
        loadDirectoryUsers(),
      ]);
      const memberUserIds = new Set(members.map((member) => member.userId));
      const normalizedQuery = searchQuery.trim().toLowerCase();

      return directoryUsers
        .filter((directoryUser) => typeof directoryUser.id === 'string')
        .filter((directoryUser) => !memberUserIds.has(directoryUser.id!))
        .filter((directoryUser) => {
          if (normalizedQuery === '') {
            return true;
          }
          const fullName =
            `${directoryUser.firstName ?? ''} ${directoryUser.lastName ?? ''}`.toLowerCase();
          const email = (directoryUser.email ?? '').toLowerCase();
          return (
            fullName.includes(normalizedQuery) ||
            email.includes(normalizedQuery)
          );
        })
        .map((directoryUser) => ({
          userId: directoryUser.id!,
          name:
            directoryUser.firstName && directoryUser.lastName
              ? `${directoryUser.firstName} ${directoryUser.lastName}`.trim()
              : (directoryUser.email ?? directoryUser.id!),
          email: directoryUser.email ?? '',
          companyRole: 'member',
          thumbnail: directoryUser.thumbnail ?? null,
        }));
    },
    [listChatMembers, loadDirectoryUsers],
  );

  const inviteChatParticipant = useCallback<
    ChatSessionsContextValue['inviteChatParticipant']
  >(
    async (sessionId, userId) => {
      await inviteParticipantMutation.mutateAsync({ sessionId, userId });
    },
    [inviteParticipantMutation],
  );

  const uninviteChatParticipant = useCallback<
    ChatSessionsContextValue['uninviteChatParticipant']
  >(
    async (sessionId, userId) => {
      const participants = await loadLatestParticipants(sessionId);
      const participant = participants.find(
        (sessionParticipant) => sessionParticipant.userId === userId,
      );

      if (!participant) {
        return;
      }

      await removeParticipantMutation.mutateAsync({
        sessionId,
        participantId: participant.id,
      });
    },
    [loadLatestParticipants, removeParticipantMutation],
  );

  const transferOwnership = useCallback<
    ChatSessionsContextValue['transferOwnership']
  >(
    async (sessionId, newOwnerUserId) => {
      await transferOwnershipMutation.mutateAsync({
        sessionId,
        newOwnerUserId,
      });
    },
    [transferOwnershipMutation],
  );

  return useMemo(
    () => ({
      getSessionById,
      createSession,
      deleteSession,
      leaveSession,
      togglePin,
      renameSession,
      setSessionSystemPrompt,
      refreshSessions,
      listChatMembers,
      listInvitableCompanyUsers,
      inviteChatParticipant,
      uninviteChatParticipant,
      transferOwnership,
    }),
    [
      getSessionById,
      createSession,
      deleteSession,
      leaveSession,
      togglePin,
      renameSession,
      setSessionSystemPrompt,
      refreshSessions,
      listChatMembers,
      listInvitableCompanyUsers,
      inviteChatParticipant,
      uninviteChatParticipant,
      transferOwnership,
    ],
  );
}
